import { toJson } from '@bufbuild/protobuf';
import { timestampDate, timestampFromDate, ValueSchema } from '@bufbuild/protobuf/wkt';
import type { DeviceDataPoint } from '../gen/proto/gateway/v1/device_data_pb';
import { Reading, ReadingWindow } from '../models/reading';
import { HistoryWindow, readHistory } from '../utils/history';
import { deviceClient, deviceDataClient } from './api-grpc';

const toReading = (point: DeviceDataPoint): Reading => ({
  time: point.time ? timestampDate(point.time) : new Date(0),
  topic: point.topic,
  payload: point.value ? toJson(ValueSchema, point.value) : null,
});

export const deviceDataApi = {
  /**
   * Every reading the device ever sent, oldest first, read as consecutive bounded time ranges back to the device's
   * creation. `onProgress` gets the running count after each request.
   */
  async getAll(deviceUuid: string, onProgress?: (count: number) => void): Promise<Reading[]> {
    const { device } = await deviceClient.getDeviceByUuid({ uuid: deviceUuid });
    const fetchWindow = async (from: Date, to: Date): Promise<HistoryWindow> => {
      const response = await deviceDataClient.getDeviceDataByTimeRange({
        deviceUuid,
        from: timestampFromDate(from),
        to: timestampFromDate(to),
      });
      return {
        readings: response.data.map((point) => ({
          ...toReading(point),
          key: `${point.time?.seconds ?? 0}.${point.time?.nanos ?? 0}|${point.topic}`,
        })),
        truncated: response.truncated,
      };
    };
    return readHistory(fetchWindow, {
      now: new Date(),
      since: device?.createdAt ? timestampDate(device.createdAt) : undefined,
      onProgress,
    });
  },

  /** Readings in [from, to), oldest first (the API returns newest first). */
  async getByTimeRange(deviceUuid: string, from: Date, to: Date): Promise<ReadingWindow> {
    const response = await deviceDataClient.getDeviceDataByTimeRange({
      deviceUuid,
      from: timestampFromDate(from),
      to: timestampFromDate(to),
    });
    return { readings: response.data.map(toReading).reverse(), truncated: response.truncated };
  },

  /** All usable positions in [from, to), oldest first, including continuation pages when the API cap is reached. */
  async getGeoByTimeRange(deviceUuid: string, from: Date, to: Date): Promise<ReadingWindow> {
    const readings = await readHistory(
      async (start, end): Promise<HistoryWindow> => {
        const response = await deviceDataClient.getDeviceGeoDataByTimeRange({
          deviceUuid,
          from: timestampFromDate(start),
          to: timestampFromDate(end),
        });
        return {
          readings: response.data.map((point) => ({
            ...toReading(point),
            key: `${point.time?.seconds ?? 0}.${point.time?.nanos ?? 0}|${point.topic}`,
          })),
          truncated: response.truncated,
        };
      },
      {
        now: to,
        since: from,
      },
    );
    return { readings, truncated: false };
  },

  /** The most recent reading the device ever sent, whatever its age. */
  async getLatest(deviceUuid: string): Promise<Reading | undefined> {
    const response = await deviceDataClient.getDeviceDataByPage({
      deviceUuid,
      pagination: { pageIndex: 1, pageSize: 1 },
    });
    return response.data[0] ? toReading(response.data[0]) : undefined;
  },
};
